import { useQuery } from "@apollo/client";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import {
  ArrowLeftIcon,
  ClipboardDocumentListIcon,
  ChatBubbleBottomCenterTextIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import styled from "styled-components";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import Panel from "../../components/Panel/Panel";
import StatCard from "../../components/StatCard/StatCard";
import Badge, { BadgeTone } from "../../components/Styled/Badge";
import {
  SurveyAnalytics as SurveyAnalyticsType,
  QuestionAnalytics,
  TextAnswer
} from "../../graphql/graphql";
import { SURVEY_ANALYTICS, SURVEY_TEXT_ANSWERS } from "../../graphql/queries-gql";

// Survey Question Type constants
const SurveyQuestionType = {
  MultipleChoice: "MultipleChoice",
  SingleChoice: "SingleChoice",
  Text: "Text",
} as const;

const TitleRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const BackIcon = styled(ArrowLeftIcon)`
  width: 18px;
  height: 18px;
`;

const SmallIcon = styled.span`
  display: inline-flex;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
`;

const SectionTitle = styled.h2`
  margin: 4px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
`;

const QuestionTitle = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const ResponseCount = styled.div`
  margin-bottom: 16px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const ChoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ChoiceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
`;

const ChoiceLabel = styled.span`
  font-size: 13.5px;
  font-weight: 500;
  color: ${(props) => props.theme.textColor};
`;

const ChoiceStats = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const ChoicePercent = styled.span`
  min-width: 52px;
  text-align: right;
  font-size: 13.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.colors.primary};
`;

const ChoiceTrack = styled.div`
  height: 8px;
  border-radius: 4px;
  background: ${(props) => props.theme.surfaceActive};
  overflow: hidden;
`;

const ChoiceFill = styled.div<{ $percent: number; $tone: "green" | "blue" | "orange" }>`
  width: ${(props) => Math.min(Math.max(props.$percent, 0), 100)}%;
  height: 100%;
  border-radius: 4px;
  background: ${(props) => props.theme.colors[props.$tone]};
`;

const NoData = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  font-size: 13px;
  color: ${(props) => props.theme.secondaryTextColor};
  background: ${(props) => props.theme.surfaceHover};
`;

const NoDataHint = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.mutedTextColor};
`;

const TextHint = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${(props) => props.theme.secondaryTextColor};

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

const AnswerList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const AnswerItem = styled.li`
  padding: 12px 0;
  border-bottom: 1px solid ${(props) => props.theme.border};

  &:last-child {
    border-bottom: 0;
  }
`;

const AnswerText = styled.div`
  font-size: 14px;
  white-space: pre-wrap;
  color: ${(props) => props.theme.textColor};
`;

const AnswerMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: 6px;
  font-size: 12px;
  color: ${(props) => props.theme.mutedTextColor};
`;

const Mono = styled.span`
  font-family: ${(props) => props.theme.monoFontFamily};
`;

const SurveyAnalytics = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const [textAnswersDialog, setTextAnswersDialog] = useState<{
    open: boolean;
    questionIndex: number | null;
    questionText: string;
  }>({ open: false, questionIndex: null, questionText: "" });

  const { data, loading, error } = useQuery<{ surveyAnalytics: SurveyAnalyticsType }>(
    SURVEY_ANALYTICS,
    {
      variables: { id: surveyId },
      skip: !surveyId,
    }
  );

  const { data: textAnswersData, loading: textAnswersLoading, error: textAnswersError } = useQuery<{
    surveyTextAnswers: TextAnswer[]
  }>(SURVEY_TEXT_ANSWERS, {
    variables: {
      id: surveyId,
      questionIndex: textAnswersDialog.questionIndex
    },
    skip: !textAnswersDialog.open || textAnswersDialog.questionIndex === null,
  });

  const renderTitle = (title: React.ReactNode) => (
    <TitleRow>
      <Tooltip title="Back to surveys">
        <IconButton
          size="small"
          aria-label="Back to surveys"
          onClick={() => navigate("/surveys")}
        >
          <BackIcon />
        </IconButton>
      </Tooltip>
      {title}
    </TitleRow>
  );

  if (loading) {
    return (
      <Page title={renderTitle("Survey")} maxWidth={1000}>
        <SummaryGrid>
          <Skeleton variant="rounded" height={96} />
          <Skeleton variant="rounded" height={96} />
        </SummaryGrid>
        <Skeleton variant="rounded" height={220} />
      </Page>
    );
  }

  if (error) {
    return (
      <Page title={renderTitle("Survey")} maxWidth={1000}>
        <ErrorAlert error={`Error loading survey analytics: ${error.message}`} />
      </Page>
    );
  }

  const survey = data?.surveyAnalytics;

  if (!survey) {
    return (
      <Page title={renderTitle("Survey")} maxWidth={1000}>
        <EmptyState Icon={ClipboardDocumentListIcon} title="Survey not found" />
      </Page>
    );
  }

  const handleViewTextAnswers = (questionIndex: number, questionText: string) => {
    setTextAnswersDialog({
      open: true,
      questionIndex,
      questionText
    });
  };

  const handleCloseTextAnswers = () => {
    setTextAnswersDialog({
      open: false,
      questionIndex: null,
      questionText: ""
    });
  };

  const getQuestionTypeTone = (type: string): BadgeTone => {
    switch (type) {
      case SurveyQuestionType.SingleChoice:
        return 'primary';
      case SurveyQuestionType.MultipleChoice:
        return 'purple';
      case SurveyQuestionType.Text:
        return 'blue';
      default:
        return 'neutral';
    }
  };

  const getQuestionTypeLabel = (type: string) => {
    switch (type) {
      case SurveyQuestionType.SingleChoice:
        return 'Single choice';
      case SurveyQuestionType.MultipleChoice:
        return 'Multiple choice';
      case SurveyQuestionType.Text:
        return 'Text answer';
      default:
        return type;
    }
  };

  const getChoiceTone = (percentage: number): "green" | "blue" | "orange" =>
    percentage > 50 ? 'green' : percentage > 25 ? 'blue' : 'orange';

  return (
    <Page
      title={renderTitle(survey.title)}
      description={survey.description || undefined}
      maxWidth={1000}
    >
      <SummaryGrid>
        <StatCard label="Total responses" value={survey.totalAnswers} />
        <StatCard
          label="Total questions"
          value={survey.questionAnalytics.length}
        />
      </SummaryGrid>

      <SectionTitle>Question analytics</SectionTitle>

      {survey.questionAnalytics.map((question: QuestionAnalytics) => (
        <Panel
          key={question.questionIndex}
          title={
            <QuestionTitle>
              Q{question.questionIndex + 1}: {question.text}
              <Badge $tone={getQuestionTypeTone(question.type)}>
                {getQuestionTypeLabel(question.type)}
              </Badge>
            </QuestionTitle>
          }
          actions={
            question.type === SurveyQuestionType.Text ? (
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={
                  <SmallIcon>
                    <EyeIcon />
                  </SmallIcon>
                }
                onClick={() => handleViewTextAnswers(question.questionIndex, question.text)}
              >
                View text answers
              </Button>
            ) : undefined
          }
        >
          <ResponseCount>{question.totalAnswers} responses</ResponseCount>

          {question.type !== SurveyQuestionType.Text && (
            <>
              {question.choiceAnalytics && question.choiceAnalytics.length > 0 ? (
                <ChoiceList>
                  {question.choiceAnalytics.map((choice, index) => (
                    <div key={index}>
                      <ChoiceHeader>
                        <ChoiceLabel>{choice.label}</ChoiceLabel>
                        <ChoiceStats>
                          <Badge>{choice.count} votes</Badge>
                          <ChoicePercent>
                            {choice.percentage.toFixed(1)}%
                          </ChoicePercent>
                        </ChoiceStats>
                      </ChoiceHeader>
                      <ChoiceTrack>
                        <ChoiceFill
                          $percent={choice.percentage}
                          $tone={getChoiceTone(choice.percentage)}
                        />
                      </ChoiceTrack>
                    </div>
                  ))}
                </ChoiceList>
              ) : (
                <NoData>
                  <span>No response data available yet</span>
                  <NoDataHint>
                    Responses will appear here once users submit their answers
                  </NoDataHint>
                </NoData>
              )}
            </>
          )}

          {question.type === SurveyQuestionType.Text && (
            <TextHint>
              <ChatBubbleBottomCenterTextIcon />
              Text responses available. Click "View text answers" to see all responses.
            </TextHint>
          )}
        </Panel>
      ))}

      <Dialog
        open={textAnswersDialog.open}
        onClose={handleCloseTextAnswers}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Text answers: {textAnswersDialog.questionText}
        </DialogTitle>
        <DialogContent dividers>
          {textAnswersLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Loader />
            </Box>
          ) : textAnswersError ? (
            <ErrorAlert
              error={`Error loading text answers: ${textAnswersError.message}`}
            />
          ) : textAnswersData?.surveyTextAnswers && textAnswersData.surveyTextAnswers.length > 0 ? (
            <AnswerList>
              {textAnswersData.surveyTextAnswers.map((answer) => (
                <AnswerItem key={answer._id}>
                  <AnswerText>{answer.answer}</AnswerText>
                  <AnswerMeta>
                    <span>
                      User ID: <Mono>{answer.userId}</Mono>
                    </span>
                    <span>{new Date(answer.createdAt).toLocaleString()}</span>
                  </AnswerMeta>
                </AnswerItem>
              ))}
            </AnswerList>
          ) : (
            <EmptyState
              title="No text responses yet"
              description="Text responses for this question will appear here once users submit them."
              style={{ padding: "40px 24px" }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={handleCloseTextAnswers}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export default SurveyAnalytics;
